import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";

import clsx from "clsx";

import { makeStyles, Paper, Box, Typography, Button, Avatar, Chip, Tooltip } from "@material-ui/core";
import { Visibility as VisibilityIcon, Lock as LockIcon, LockOpen as LockOpenIcon } from "@material-ui/icons";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import AgentModeIndicator from "../AgentModeIndicator";
import AgentSuggestionsPanel from "../AgentSuggestionsPanel";
import AgentSuggestionsModal from "../AgentSuggestionsModal";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";
import { useAgentMode } from "../../hooks/useAgentMode";
import { acceptAgentSuggestion, rejectAgentSuggestion, sendAgentSuggestion } from "../../services/agentService";

import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { isNil } from 'lodash';
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import TicketTimer from "../TicketTimer";

const drawerWidth = 320;
const drawerWidthSmall = 280; // Para telas menores que 1366px

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // Responsividade para telas menores
    [theme.breakpoints.down(1366)]: {
      marginRight: -drawerWidthSmall,
    },
    [theme.breakpoints.down('sm')]: {
      marginRight: -280,
    },
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  chatContainer: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },

  messagesContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  observerModeBar: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.type === "dark" ? "#2a1e05" : "#fff8e1",
    borderTop: `1px solid ${theme.palette.type === "dark" ? "#5d4037" : "#ffe082"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.05)",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(1),
      alignItems: "flex-start",
    },
  },

  observerModeUnlockedBar: {
    padding: theme.spacing(0.75, 2),
    backgroundColor: theme.palette.type === "dark" ? "#332200" : "#fff3e0",
    borderTop: `1px solid ${theme.palette.type === "dark" ? "#664400" : "#ffcc80"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(0.5),
      alignItems: "flex-start",
    },
  },

  suggestionsContainer: {
    width: 350,
    flexShrink: 0,
    borderLeft: `1px solid ${theme.palette.divider}`,
    // Responsividade para telas menores
    [theme.breakpoints.down(1366)]: {
      width: 300,
    },
    [theme.breakpoints.down('md')]: {
      width: 250,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none', // Oculta em telas muito pequenas
    },
  },

  agentModeHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const { user, socket } = useContext(AuthContext);
  const { setTabOpen } = useContext(TicketsContext);


  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [chatUnlocked, setChatUnlocked] = useState(false);
  const { companyId } = user;

  // 👁️ Trava de Digitação / Modo Observador:
  // Se o ticket pertencer a outro atendente (não for meu nem grupo), o chat abre desligado
  const isOtherUserTicket = Boolean(
    ticket?.userId &&
    ticket.userId !== user?.id &&
    !ticket.isGroup &&
    ticket.status !== "group"
  );

  useEffect(() => {
    setChatUnlocked(false);
  }, [ticketId]);

  // Agent mode hook
  const {
    agentMode,
    isAgentActive,
    canTakeover,
    suggestions,
    isLoadingSuggestions,
    handleTakeover,
    setSuggestions,
  } = useAgentMode(ticket, {
    onNewSuggestions: (newSuggestions) => {
      setShowSuggestionsModal(true);
    }
  });


  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {

          if (!isNil(ticketId) && ticketId !== "undefined") {

            const { data } = await api.get("/tickets/u/" + ticketId);

            setContact(data.contact);
            // setWhatsapp(data.whatsapp);
            // setQueueId(data.queueId);
            setTicket(data);
            if (["pending", "open", "group"].includes(data.status)) {
              setTabOpen(data.status);
            }
            setLoading(false);
          }
        } catch (err) {
          history.push("/tickets");   // correção para evitar tela branca uuid não encontrado Feito por Altemir 16/08/2023
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, history]);

  useEffect(() => {
    if (!ticket && !ticket.id && ticket.uuid !== ticketId && ticketId === "undefined") {
      return;
    }

    if (user.companyId && socket && typeof socket.on === 'function' && typeof socket.off === 'function') {
      const onConnectTicket = () => {
        if (socket && typeof socket.emit === 'function') {
          socket.emit("joinChatBox", `${ticket.id}`);
        }
      }

      const onCompanyTicket = (data) => {
        if (data.action === "update" && data.ticket?.id === (ticket && ticket.id)) {
          const updatedTicket = data.ticket;

          // Se for ticket individual (não grupo) e possuir titular atribuído
          // Verificar se o usuário logado foi removido dos participantes
          const isOwner = updatedTicket.userId === user?.id;
          const isAdmin = user?.profile === "admin";
          const isAcceptedParticipant = updatedTicket.participants?.some(
            (p) => p.userId === user?.id && p.status === "accepted"
          );

          if (
            !updatedTicket.isGroup &&
            updatedTicket.status !== "group" &&
            updatedTicket.userId &&
            !isOwner &&
            !isAdmin &&
            !isAcceptedParticipant
          ) {
            toastUtils.warning("Você foi removido deste atendimento.", "Aviso");
            try {
              if (socket && typeof socket.emit === 'function') {
                socket.emit("joinChatBoxLeave", `${ticket.id}`);
              }
            } catch (err) {
              console.error(err);
            }
            history.push("/tickets");
            return;
          }

          setTicket(updatedTicket);
        }

        if (data.action === "delete" && data.ticketId === (ticket && ticket.id)) {
          history.push("/tickets");
        }
      };

      const onParticipantLeft = (data) => {
        if (data.ticketId === (ticket && ticket.id) && data.userId === user?.id) {
          toastUtils.warning("Você foi removido deste atendimento.", "Aviso");
          try {
            if (socket && typeof socket.emit === 'function') {
              socket.emit("joinChatBoxLeave", `${ticket.id}`);
            }
          } catch (err) {
            console.error(err);
          }
          history.push("/tickets");
        }
      };

      const onCompanyContactTicket = (data) => {
        if (data.action === "update") {
          setContact((prevState) => {
            if (prevState.id === (data.contact && data.contact.id)) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
        }
      };

      try {
        socket.on("connect", onConnectTicket)
        socket.on(`company-${companyId}-ticket`, onCompanyTicket);
        socket.on(`company-${companyId}-ticketParticipantLeft`, onParticipantLeft);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicket);

        return () => {
          try {
            if (socket && typeof socket.emit === 'function') {
              socket.emit("joinChatBoxLeave", `${ticket.id}`);
            }
            socket.off("connect", onConnectTicket);
            socket.off(`company-${companyId}-ticket`, onCompanyTicket);
            socket.off(`company-${companyId}-ticketParticipantLeft`, onParticipantLeft);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicket);
          } catch (error) {
            console.error('Erro ao remover listeners do Ticket:', error);
          }
        };
      } catch (error) {
        console.error('Erro ao adicionar listeners do Ticket:', error);
      }
    }
  }, [ticketId, ticket, history, socket, companyId]);


  const handleDrawerOpen = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleQuickMessageSelect = (quickMessage) => {
    try {
      if (quickMessage.message) {
        // Disparar evento que o MessageInput vai escutar
        const event = new CustomEvent('insertQuickMessage', {
          detail: { message: quickMessage.message }
        });
        window.dispatchEvent(event);
        
      }
      
      if (quickMessage.mediaPath) {
        // Tratar mídia se necessário
      }
    } catch (error) {
      console.error("Erro ao inserir resposta rápida:", error);
      toastUtils.error("Erro ao inserir resposta rápida", "Erro Resposta");
    }
  };

  // Handle agent suggestion actions
  const handleAcceptSuggestion = useCallback(async (suggestion) => {
    try {
      // Insert suggestion text into MessageInput
      const event = new CustomEvent('insertAgentSuggestion', {
        detail: { message: suggestion.message }
      });
      window.dispatchEvent(event);
      
      // Notify backend about acceptance
      await acceptAgentSuggestion(ticket.id, suggestion);
    } catch (error) {
      console.error("Error accepting suggestion:", error);
      toastError(error);
    }
  }, [ticket.id]);

  const handleRejectSuggestion = useCallback(async (suggestion) => {
    try {
      await rejectAgentSuggestion(ticket.id, suggestion.id);
    } catch (error) {
      console.error("Error rejecting suggestion:", error);
      toastError(error);
    }
  }, [ticket.id]);

  const handleSendSuggestion = useCallback(async (suggestion) => {
    try {
      await sendAgentSuggestion(ticket.id, suggestion);
    } catch (error) {
      console.error("Error sending suggestion:", error);
      toastError(error);
    }
  }, [ticket.id]);

  // Função para abrir modal manualmente
  const handleOpenSuggestionsModal = useCallback(() => {
    setShowSuggestionsModal(true);
  }, []);

  const renderChatContent = () => {
    const showSuggestions = (agentMode === "AGENT_ASSISTANT" || agentMode === "agent_assistant") && (suggestions.length > 0 || isLoadingSuggestions);

    return (
      <Box className={classes.chatContainer}>
        <Box className={classes.messagesContainer}>
          <MessagesList
            ticket={ticket}
            ticketIdProp={ticket.id}
            isGroup={ticket.isGroup}
            onDrop={setDragDropFiles}
            whatsappId={ticket.whatsappId}
            queueId={ticket.queueId}
            channel={ticket.channel}
            ticketStatus={ticket.status}
            agentMode={agentMode}
            isAgentActive={isAgentActive}
          />
          {isOtherUserTicket && !chatUnlocked ? (
            /* Modo Observador - Chat Desligado */
            <Paper square elevation={0} className={classes.observerModeBar}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar style={{ backgroundColor: "#ff9800", width: 40, height: 40 }}>
                  <VisibilityIcon style={{ color: "#fff", fontSize: 22 }} />
                </Avatar>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#e65100" }}>
                      Modo Observador Ativo • Chat Desligado
                    </Typography>
                    <Chip
                      label="Apenas Leitura"
                      size="small"
                      style={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: "#ffe0b2",
                        color: "#bf360c",
                        fontWeight: 600
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary" style={{ fontSize: "0.82rem" }}>
                    Atendimento de <strong>{ticket.user?.name || "outro atendente"}</strong>. A digitação está desligada para evitar mensagens acidentais.
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LockOpenIcon />}
                  onClick={() => setChatUnlocked(true)}
                  style={{
                    backgroundColor: "#e65100",
                    color: "#fff",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: 20,
                    padding: "6px 16px"
                  }}
                >
                  Ligar Chat / Intervir
                </Button>
              </Box>
            </Paper>
          ) : (
            <>
              {isOtherUserTicket && chatUnlocked && (
                <Box className={classes.observerModeUnlockedBar}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LockOpenIcon style={{ color: "#e65100", fontSize: 18 }} />
                    <Typography variant="caption" style={{ color: "#e65100", fontWeight: 700 }}>
                      Chat Ligado: Você está intervindo no atendimento de {ticket.user?.name || "outro atendente"}. As mensagens serão enviadas ao cliente.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LockIcon />}
                    onClick={() => setChatUnlocked(false)}
                    style={{
                      color: "#e65100",
                      borderColor: "#ff9800",
                      height: 26,
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 14
                    }}
                  >
                    Desligar Chat (Modo Observador)
                  </Button>
                </Box>
              )}
              <MessageInput
                ticket={ticket}
                contact={contact}
                ticketId={ticket.id}
                ticketStatus={ticket.status}
                ticketChannel={ticket.channel}
                droppedFiles={dragDropFiles}
                contactId={contact.id}
                whatsappId={ticket.whatsappId}
                agentMode={agentMode}
                isAgentActive={isAgentActive}
              />
            </>
          )}
        </Box>
        
        {showSuggestions && (
          <Box className={classes.suggestionsContainer}>
            <AgentSuggestionsPanel
              suggestions={suggestions}
              isLoading={isLoadingSuggestions}
              onAcceptSuggestion={handleAcceptSuggestion}
              onRejectSuggestion={handleRejectSuggestion}
              onSendSuggestion={handleSendSuggestion}
            />
          </Box>
        )}
      </Box>
    );
  };


  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        {/* <div id="TicketHeader"> */}
        <TicketHeader loading={loading}>
          {ticket.contact !== undefined && (
            <div id="TicketHeader">
              <TicketInfo
                contact={contact}
                ticket={ticket}
                onClick={handleDrawerOpen}
              />
            </div>
          )}
          <TicketTimer ticket={ticket} visible={ticket?.status === "open"} />
                
        <TicketActionButtons
          ticket={ticket}
          contact={contact}
          onQuickMessageSelect={handleQuickMessageSelect}
          onOpenDrawer={handleDrawerOpen}
          isOtherUserTicket={isOtherUserTicket}
          chatUnlocked={chatUnlocked}
          onToggleChatLock={() => setChatUnlocked((prev) => !prev)}
        />
        </TicketHeader>
        {/* </div> */}
        {!ticket?.isGroup && !contact?.isGroup && ticket?.status !== "group" && (
          <Paper>
            <TagsContainer contact={contact} />
          </Paper>
        )}
        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              {renderChatContent()}
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
      </Paper>

      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />

      {/* Modal de Sugestões de IA */}
      <AgentSuggestionsModal
        open={showSuggestionsModal}
        onClose={() => setShowSuggestionsModal(false)}
        suggestions={suggestions}
        isLoading={isLoadingSuggestions}
        onAcceptSuggestion={handleAcceptSuggestion}
        onRejectSuggestion={handleRejectSuggestion}
        onSendSuggestion={handleSendSuggestion}
      />

    </div>
  );
};

export default Ticket;